import React from "react";
import { Link } from "react-router-dom";
import Reveal from "./Reveal";
import { useI18n } from "../data/i18n";

const DiamondDivider = () => (
  <div className="flex items-center justify-center gap-2 my-4">
    <div className="w-12 h-px bg-gradient-to-r from-transparent to-[#e4c590]"></div>
    <span className="diamond-mark"></span>
    <span className="diamond-mark opacity-60"></span>
    <span className="diamond-mark"></span>
    <div className="w-12 h-px bg-gradient-to-l from-transparent to-[#e4c590]"></div>
  </div>
);

/**
 * Text-only menu page (no item images). Used for Lunch, Drinks, Alcohol.
 * Renders sections with item rows (name on left, price on right, weight + description below).
 */
const TextMenuPage = ({ label, title, subtitle, sections, largeText = false }) => {
  const { t } = useI18n();

  return (
    <div className="bg-[#0a0a0a] pt-32 pb-24 min-h-screen relative overflow-hidden">
      <div className="max-w-4xl mx-auto px-6 relative">
        <Reveal direction="up">
          <div className="text-center mb-16">
            {label && <p className="section-label">{label}</p>}
            <h1 className="display text-5xl md:text-7xl text-[#f5f0e6] mt-3">{title}</h1>
            <DiamondDivider />
            {subtitle && <p className="text-[#cfc7b8] italic">{subtitle}</p>}
          </div>
        </Reveal>

        {sections.map((section, sIdx) => (
          <div key={sIdx} className="mb-16">
            <Reveal direction="up" delay={sIdx * 80}>
              <div className="text-center mb-10">
                <h2 className="display text-3xl md:text-4xl text-[#e4c590] uppercase tracking-widest">
                  {section.title}
                </h2>
                <div className="flex items-center justify-center gap-2 mt-4">
                  <span className="diamond-mark opacity-60"></span>
                  <span className="diamond-mark"></span>
                  <span className="diamond-mark opacity-60"></span>
                </div>
              </div>
            </Reveal>

            <div className="space-y-6">
              {section.items.map((item, idx) => (
                <Reveal key={idx} direction="up" delay={Math.min(idx, 6) * 50}>
                  <div className="group">
                    <div className="flex items-baseline justify-between gap-6 mb-1">
                      <h3 className={`display ${largeText ? "text-2xl md:text-3xl" : "text-xl md:text-2xl"} text-[#f5f0e6] group-hover:text-[#e4c590] transition-colors leading-snug`}>
                        {item.name}
                      </h3>
                      <div className="flex-shrink-0 flex items-center gap-3">
                        <span className="hidden sm:block flex-1 min-w-[60px] border-b border-dotted border-[#2a2a2a]"></span>
                        <span className={`text-[#e4c590] ${largeText ? "text-base md:text-lg" : "text-sm md:text-base"} whitespace-nowrap font-medium`}>
                          {item.price}
                        </span>
                      </div>
                    </div>
                    {item.desc && (
                      <p className="text-[#a8a297] text-sm leading-relaxed mb-1 italic">{item.desc}</p>
                    )}
                    {item.weight && (
                      <p className="text-[#7a7468] text-xs uppercase tracking-widest">{item.weight}</p>
                    )}
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        ))}

        <Reveal direction="up">
          <div className="text-center mt-12">
            <Link to="/menu" className="cb-btn cb-btn-outline inline-block">
              <span className="cb-btn-text cb-btn-text-1">← {t("menu.back")}</span>
              <span className="cb-btn-text cb-btn-text-2" aria-hidden="true">← {t("menu.back")}</span>
            </Link>
          </div>
        </Reveal>
      </div>
    </div>
  );
};

export default TextMenuPage;
